import './DateTime.scss'
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { clx } from '../clx'
import { Tooltip } from '../Tooltip'
import { VisuallyHidden } from '../VisuallyHidden'
import { dateTimeParts, machineDateTime, parseDate, type DateTimeFormat } from './format'

export interface DateTimeProps {
  /** An ISO string, epoch ms or a Date (`DateInput`); anything else renders nothing. */
  value: unknown
  /** Default `DD-MM-YYYY HH:mm:ss`. */
  format?: DateTimeFormat
  /** Opt-in: a click or tap on the date copies the shown text. Default false. */
  isCopyable?: boolean
  className?: string
}

/** How long the "Copied" tooltip shows, ms. */
const COPIED_FOR = 1600

/**
 * A date in one of the app's formats (ADR 0026): a `<time>` element that
 * inherits the surrounding font and looks like the text around it. Nothing
 * renders for an empty or invalid value.
 *
 * `isCopyable`: a click or tap on the date copies the shown text. Nothing
 * announces this beforehand (no cursor, hover style or tooltip); afterwards a
 * small "Copied" tooltip and a polite live region confirm it. The click never
 * reaches a clickable row around it. Keyboard users copy by selecting the
 * text, as with any other text.
 */
export function DateTime({
  value,
  format = 'DD-MM-YYYY HH:mm:ss',
  isCopyable = false,
  className
}: DateTimeProps) {
  const [isCopied, setCopied] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  useEffect(
    () => () => {
      window.clearTimeout(timer.current)
    },
    []
  )

  const date = parseDate(value)
  if (!date) return null
  const parts = dateTimeParts(date, format)
  const text = parts.time === undefined ? parts.date : `${parts.date} ${parts.time}`

  const copy = (event: MouseEvent<HTMLElement>) => {
    // A date in a clickable row copies; it does not open the row.
    event.stopPropagation()
    // A drag that selected part of the text is a selection, not a copy.
    const selection = window.getSelection()?.toString() ?? ''
    if (selection !== '' && selection !== text) return
    if (typeof navigator === 'undefined' || !('clipboard' in navigator)) return
    void navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => {
          setCopied(false)
        }, COPIED_FOR)
      },
      () => undefined
    )
  }

  const time = (
    <time
      dateTime={machineDateTime(value, date, format)}
      className={clx('date-time', className)}
      {...(isCopyable && { onClick: copy, 'data-state': isCopied ? 'copied' : undefined })}
    >
      <span className="date-time__date">{parts.date}</span>
      {parts.time !== undefined && (
        <>
          {' '}
          <span className="date-time__time">{parts.time}</span>
        </>
      )}
    </time>
  )
  if (!isCopyable) return time

  return (
    <>
      <Tooltip content="Copied" isOpen={isCopied}>
        {time}
      </Tooltip>
      <VisuallyHidden aria-live="polite">{isCopied ? `${text} copied` : ''}</VisuallyHidden>
    </>
  )
}
